/**
 * PRD Editor - Editable section list for PRD documents
 * Story 227: Edit PRDs, tech designs and tasks
 * Story 239: Auto-generate anchors for coverage linkages
 * Story 232: Undo section delete with ButterBar
 *
 * Wraps SectionList with document mutations for PRD editing.
 * PRD sections have auto-generated anchors that design sections reference via `covers`.
 */

import { useState, useCallback, useRef, useMemo } from 'react';
import { SectionList } from '../editor/SectionList';
import { ButterBar } from '../editor/ButterBar';
import { useDocumentMutations } from '../../hooks/useDocumentMutations';
import { useRestoreSection } from '../../hooks/useSectionHistory';
import { generateAnchorFromTitle } from '../../utils/document';
import { generateAnchor } from '@ceetrix/shared';
import type { DocumentSection, SectionMetadata, SectionType } from '../../types/document';
import type { PRDSectionType } from '../../types/document';

/** Info about a recently deleted section for undo */
interface DeletedSectionInfo {
  id: string;
  title: string;
}

interface PRDEditorProps {
  projectSlug: string;
  storyId: string;
  sections: DocumentSection[];
  /** Coverage metadata showing which design capabilities cover each PRD section */
  metadata?: Record<string, SectionMetadata>;
  /** Called when a section is selected (for URL state persistence) */
  onSectionSelect?: (sectionKey: string) => void;
  /** Called when user clicks a design capability in coverage indicator */
  onNavigateToDesign?: (anchor: string) => void;
}

export function PRDEditor({
  projectSlug,
  storyId,
  sections: initialSections,
  metadata = {},
  onSectionSelect,
  onNavigateToDesign,
}: PRDEditorProps) {
  const [sections, setSections] = useState<DocumentSection[]>(initialSections);
  const [deletedSection, setDeletedSection] = useState<DeletedSectionInfo | null>(null);
  const sectionSeqRef = useRef(Math.max(0, ...initialSections.map((s) => s.seq)));

  // Auto-activate body for first section when it's empty (just created via EmptyDocState)
  const initialActiveField = useMemo(() => {
    if (initialSections.length === 1 && !initialSections[0].body) {
      return { sectionKey: initialSections[0].section_key, field: 'body' as const };
    }
    return null;
  }, []); // Only compute on mount

  const {
    updateSection,
    createSection,
    deleteSection,
    reorderSections,
  } = useDocumentMutations(projectSlug, storyId, 'prd');

  // Story 232: Restore section mutation for undo
  const restoreSection = useRestoreSection(projectSlug, storyId, 'prd');

  // Save section body with debounced autosave
  const handleSave = useCallback(
    async (sectionKey: string, body: string) => {
      // Optimistic update
      setSections((prev) =>
        prev.map((s) => (s.section_key === sectionKey ? { ...s, body } : s))
      );
      await updateSection.mutateAsync({ sectionKey, updates: { body } });
    },
    [updateSection]
  );

  // Update section title
  const handleTitleChange = useCallback(
    (sectionKey: string, newTitle: string) => {
      // Optimistic update
      setSections((prev) =>
        prev.map((s) => (s.section_key === sectionKey ? { ...s, title: newTitle } : s))
      );
      updateSection.mutate({ sectionKey, updates: { title: newTitle } });
    },
    [updateSection]
  );

  // Handle drag-and-drop reorder
  const handleReorder = useCallback(
    (activeKey: string, overKey: string) => {
      const activeIndex = sections.findIndex((s) => s.section_key === activeKey);
      const overIndex = sections.findIndex((s) => s.section_key === overKey);

      if (activeIndex === -1 || overIndex === -1) return;

      // Create new order
      const newSections = [...sections];
      const [removed] = newSections.splice(activeIndex, 1);
      newSections.splice(overIndex, 0, removed);

      // Renumber seq values
      const reorderedSections = newSections.map((s, i) => ({ ...s, seq: i + 1 }));
      setSections(reorderedSections);

      // Send new order to server
      const newOrder = reorderedSections.map((s) => s.section_key);
      reorderSections.mutate(newOrder);
    },
    [sections, reorderSections]
  );

  // Insert new section after specified section (or at start if null)
  // PRD sections get auto-generated anchors for design sections to reference via `covers`
  const handleInsert = useCallback(
    (afterKey: string | null): string => {
      sectionSeqRef.current += 1;
      const newSeq = sectionSeqRef.current;
      const newKey = `section-${newSeq}`;
      const defaultTitle = 'New Section';
      const autoAnchor = generateAnchorFromTitle(defaultTitle, newSeq);

      const newSection: DocumentSection = {
        section_key: newKey,
        title: defaultTitle,
        body: '',
        seq: newSeq,
        anchor: autoAnchor,
      };

      // Find insertion point
      if (afterKey === null) {
        setSections([newSection, ...sections]);
      } else {
        const afterIndex = sections.findIndex((s) => s.section_key === afterKey);
        const newSections = [...sections];
        newSections.splice(afterIndex + 1, 0, newSection);
        setSections(newSections);
      }

      // Create on server - update local state with returned ID (Story 232)
      createSection.mutate(
        {
          title: newSection.title,
          body: newSection.body,
          section_key: newKey,
          anchor: autoAnchor,
        },
        {
          onSuccess: (result) => {
            if (result.id) {
              setSections((prev) =>
                prev.map((s) =>
                  s.section_key === newKey ? { ...s, id: result.id } : s
                )
              );
            }
          },
        }
      );

      return newKey;
    },
    [sections, createSection]
  );

  // Delete a section - Story 232: capture info for undo
  const handleDelete = useCallback(
    (sectionKey: string) => {
      // Find the section to capture its info before removing
      const section = sections.find((s) => s.section_key === sectionKey);
      if (section?.id) {
        setDeletedSection({ id: section.id, title: section.title });
      }
      setSections((prev) => prev.filter((s) => s.section_key !== sectionKey));
      deleteSection.mutate(sectionKey);
    },
    [sections, deleteSection]
  );

  // Story 232: Handle undo action from ButterBar
  const handleUndo = useCallback(() => {
    if (deletedSection) {
      restoreSection.mutate(deletedSection.id);
      setDeletedSection(null);
    }
  }, [deletedSection, restoreSection]);

  // Story 232: Dismiss ButterBar
  const handleDismissButterBar = useCallback(() => {
    setDeletedSection(null);
  }, []);

  // Story 293: Update PRD section type with anchor management
  const handleSectionTypeChange = useCallback(
    (sectionKey: string, newType: SectionType) => {
      const section = sections.find((s) => s.section_key === sectionKey);
      if (!section) return;

      // Build updates object
      const updates: { section_type: SectionType; anchor?: string | null } = {
        section_type: newType,
      };

      let newAnchor = section.anchor;

      // If changing to 'requirement' and no anchor exists, generate one
      if (newType === 'requirement' && !section.anchor) {
        const existingAnchors = sections
          .filter((s) => s.section_key !== sectionKey && s.anchor)
          .map((s) => s.anchor as string);
        const { anchor } = generateAnchor(section.title, existingAnchors);
        updates.anchor = anchor;
        newAnchor = anchor;
      }
      // Note: Changing to 'informational' preserves existing anchor (per PRD design)

      // Optimistic update
      setSections((prev) =>
        prev.map((s) =>
          s.section_key === sectionKey
            ? { ...s, section_type: newType as PRDSectionType, anchor: newAnchor }
            : s
        )
      );

      updateSection.mutate({
        sectionKey,
        updates,
      });
    },
    [sections, updateSection]
  );

  return (
    <div className="prd-editor" data-testid="prd-editor">
      <SectionList
        sections={sections}
        metadata={metadata}
        docType="prd"
        onReorder={handleReorder}
        onSave={handleSave}
        onTitleChange={handleTitleChange}
        onInsert={handleInsert}
        onDelete={handleDelete}
        onSectionSelect={onSectionSelect}
        onNavigateToDesign={onNavigateToDesign}
        initialActiveField={initialActiveField}
        onSectionTypeChange={handleSectionTypeChange}
      />

      {/* Story 232: Undo delete toast */}
      {deletedSection && (
        <ButterBar
          message={`Deleted "${deletedSection.title}"`}
          onUndo={handleUndo}
          onDismiss={handleDismissButterBar}
          undoInProgress={restoreSection.isPending}
        />
      )}
    </div>
  );
}
